import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'app/core/auth/auth.service';
import { UpdateUserCommand } from 'app/modules/users/api/commands/user-command.types';
import { UserStore } from 'app/modules/users/store/user.store';
import { Role } from 'app/shared/enums/role';
import { MatDialogService } from 'app/shared/services/material/mat-dialog.service';
import { MatSnackBarService } from 'app/shared/services/material/mat-snack-bar.service';
import { PermissionsService } from 'app/shared/services/permissions.service';
import { User } from 'app/shared/types/user.types';
import { error, hasError } from 'app/shared/utils/app-error-utils';
import { currentPasswordRequiredValidator } from 'app/shared/validators/current-password-required-validator';
import { passwordMatchValidator } from 'app/shared/validators/password-match-validator';

@Injectable()
export class UserDialogViewModel {
    private readonly _fb = inject(FormBuilder);
    private readonly _dialogRef = inject(MatDialogRef<unknown>);
    private readonly _userStore = inject(UserStore);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _snackBarService = inject(MatSnackBarService);
    private readonly _dialogService = inject(MatDialogService);
    private readonly _permissionsService = inject(PermissionsService);
    private readonly _authService = inject(AuthService);
    private readonly _hasAttemptedSave = signal(false);
    private readonly _passwordChanged = signal(false);

    readonly user = this._userStore.user;
    readonly isUpdating = computed(() => this._userStore.isUpdatingUser());
    readonly hasError = computed(() => hasError(this.error()));
    readonly error = computed(() => error([
        ...this._userStore.errors().loadUser,
        ...this._userStore.errors().updateUser
    ]));
    readonly errorTitle = computed<string>(() => {
        if (this._userStore.hasLoadUserError()) return 'Error loading user';
        if (this._userStore.hasUpdateUserError()) return 'Error saving user';
        return 'Error';
    });
    readonly hasMfaEnabledWritePermission = computed(() =>
        this._permissionsService.checkPermissions({ minimumRole: Role.Administrator })
    );
    readonly userForm: UntypedFormGroup = this._createForm();

    constructor() {
        effect(() => this._populateForm(this.user()!));
        effect(() => this._handleSaved());
    }

    init(): void {
        this._userStore.clearErrors();
        this._userStore.loadUser();
        this._setupValidators();
    }

    save(): void {
        if (this.userForm.invalid) {
            this.userForm.markAllAsTouched();
            return;
        }

        const formValue = this.userForm.value;
        const updateRequest: UpdateUserCommand = {
            firstName: formValue.firstName,
            surname: formValue.surname,
            phoneNumber: formValue.phoneNumber,
            position: formValue.position,
            mfaEnabled: this.hasMfaEnabledWritePermission() ? formValue.mfaEnabled : undefined,
            currentPassword: formValue.currentPassword || undefined,
            newPassword: formValue.newPassword || undefined,
            confirmPassword: formValue.confirmPassword || undefined
        };

        this._userStore.clearErrors();
        this._hasAttemptedSave.set(true);
        this._passwordChanged.set(!!updateRequest.newPassword);
        this._userStore.updateUser(updateRequest);
    }

    close(): void {
        this._dialogService.close(this._dialogRef, true);
    }

    private _createForm(): UntypedFormGroup {
        return this._fb.group({
            firstName: ['', [Validators.required, Validators.maxLength(50)]],
            surname: ['', [Validators.required, Validators.maxLength(50)]],
            email: [{ value: '', disabled: true }],
            phoneNumber: ['', Validators.maxLength(256)],
            position: ['', Validators.maxLength(50)],
            currentPassword: [''],
            newPassword: ['', [Validators.minLength(8), Validators.maxLength(50)]],
            confirmPassword: [''],
            mfaEnabled: [false]
        });
    }

    private _populateForm(user: User): void {
        this.userForm.patchValue({
            firstName: user.firstName,
            surname: user.surname,
            email: user.email,
            phoneNumber: user.phoneNumber || '',
            position: user.position || '',
            mfaEnabled: user.mfaEnabled || false
        });
    }

    private _handleSaved(): void {
        const isUpdating = this.isUpdating();
        const hasError = this.hasError();
        const hasAttemptedSave = this._hasAttemptedSave();

        if (!isUpdating && !hasError && hasAttemptedSave) {
            this._hasAttemptedSave.set(false);

            if (this._passwordChanged()) {
                this._authService.signOut();
                return;
            }

            this._snackBarService.open('User saved successfully', 2500, 'fill', 'success');
            this.close();
        }
    }

    private _setupValidators(): void {
        const currentPasswordControl = this.userForm.get('currentPassword') as AbstractControl;
        const newPasswordControl = this.userForm.get('newPassword') as AbstractControl;
        const confirmPasswordControl = this.userForm.get('confirmPassword') as AbstractControl;

        currentPasswordControl?.setValidators([
            currentPasswordRequiredValidator(newPasswordControl)
        ]);

        newPasswordControl?.valueChanges
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                currentPasswordControl?.updateValueAndValidity();
                currentPasswordControl?.markAsTouched();
                confirmPasswordControl?.updateValueAndValidity();
            });

        confirmPasswordControl?.setValidators([
            passwordMatchValidator(newPasswordControl)
        ]);
    }
}
